<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            defaultOptionText="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field='id'
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="model.unidadeId == null"
                            :options="infraestruturas"
                            defaultOptionText="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field='id'
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="model.infraestruturaId == null"
                            :options="profissionais"
                            defaultOptionText="Todos"
                            label="Profissional"
                            show-default-option
                            text-field="nome"
                            v-model="model.profissionalId"
                            value-field='id'
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            label="Tipo"
                            :options="form.tiposDocumento"
                            default-option-text="Todos"
                            v-model="model.tipoDocumento"
                            show-default-option
                        />
                    </rkm-col>

                    <rkm-col
                        class="text-nowrap"
                        cols="auto"
                    >
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>
                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>
                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedCols"
                        value-field="key"
                    />
                </rkm-dropdown>
                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
                <rkm-button
                    :disabled="itens.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                sort-by="dataHora"
            />
        </rkm-area>
    </div>
</template>

<script>
// Mixins.
import { repository, error, data, object, stringCase, formats, file } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedCols.includes(c.key))
        },
        validations() {

            return {
                periodo: { required: true },
            }
        },
        infraestruturas() {

            if (this.model.unidadeId !== null) {

                return this.form.unidades.find(u => u.id === this.model.unidadeId).infraestruturas
            }

            return []
        },
        profissionais() {

            if (this.model.infraestruturaId !== null && this.infraestruturas.length > 0) {

                const infrasestrutura = this.infraestruturas.find(i => i.id === this.model.infraestruturaId)

                return infrasestrutura ? infrasestrutura.profissionais : []
            }

            return []
        },
    },
    created() {

        this.repositories = {
            relatorioDocumentoNaoAssinado: this.getConectaSusRepository('prontoAtendimento.relatorioDocumentoNaoAssinado'),
            impressao: this.getConectaSusRepository('impressao.prontoAtendimento'),
            exportacao: this.getConectaSusRepository('exportacao.prontoAtendimento'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            defaultModel: null,
            model: null,
            selectedAll: true,
            indeterminate: false,
            selectedCols: [
                'unidade',
                'infraestrutura',
                'tipo',
                'faaNumero',
            ],
            columns: [
                {
                    key: 'dataHora',
                    label: 'Data/hora',
                    sortable: true,
                    class: 'col-min text-center',
                    formatter: value => this.formatDateTime(value),
                    filterByFormatted: true,
                    fixed: true
                },
                { key: 'unidade', label: 'Unidade', sortable: true },
                { key: 'infraestrutura', label: 'Local de atendimento', sortable: true },
                { key: 'profissional', label: 'Profissional', sortable: true, fixed: true },
                { key: 'tipo', label: 'Tipo', sortable: true, fixed: true },
                { key: 'faaNumero', label: 'FAA', sortable: true, class: 'col-min text-center' },
            ],
            itens: [],
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.relatorioDocumentoNaoAssinado.list()
                .then(({ data }) => {

                    data.model.periodo.start = this.getDateObject(data.model.periodo.start)
                    data.model.periodo.end = this.getDateObject(data.model.periodo.end)

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model
                    this.defaultModel = this.clone(data.model)

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {

            this.$watch('model.unidadeId', function () {

                this.model.infraestruturaId = null
            })

            this.$watch('model.infraestruturaId', function () {

                this.model.profissionalId = null
            })
        },
        search() {

            this.setLoading()

            this.repositories.relatorioDocumentoNaoAssinado.index(this.prepareData())
                .then(({ data }) => {

                    this.itens = data.documentos
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.model = this.clone(this.defaultModel)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioDocumentoNaoAssinado(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {

            this.setLoading()

            this.repositories.exportacao.exportDocumentoNaoAssinado(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.selectedCols = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.formateDates(data.periodo, ['start', 'end'], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedCols = selected ? this.optionalColumns.map(c => c.key) : []
        },
    },
    mixins: [repository, error, data, object, stringCase, formats, file],
     watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
            }
        },
        selectedCols(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
    .dropdown >>> .auto-scroll {
        max-height: 300px;
        overflow-y: auto;
        width: 220px;
    }
</style>